<template>
    <div class="rrule-generator">
        <div class="left">
            <h4 class="title">{{ $t("automation.schedulerPlanner") }}</h4>
            <div class="item-wrapper">
                <span class="label">{{ $t("ticimax.frequency") }}:</span>
                <div class="options">
                    <a-radio-group v-model="rrule.freq" :options="freqList" />
                </div>
            </div>
            <div class="item-wrapper">
                <span class="label">{{ $t("message.startDate") }}:</span>
                <div class="options">
                    <a-date-picker v-model="rrule.startDate" showTime />
                </div>
            </div>
            <div class="item-wrapper">
                <span class="label">{{ $t("message.endDate") }}:</span>
                <div class="options">
                    <a-date-picker v-model="rrule.until" showTime />
                </div>
            </div>
            <div class="item-wrapper">
                <span class="label">{{ $t("message.count") }}:</span>
                <div class="options">
                    <a-input-number v-model="rrule.count" :min="1" :max="365" />
                </div>
            </div>
            <div class="item-wrapper">
                <span class="label">{{ $t("ticimax.interval") }}:</span>
                <div class="options">
                    <a-input-number v-model="rrule.interval" />
                </div>
            </div>
            <div v-if="showByWeekDay" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byWeekDay") }}:</span>
                <div class="options">
                    <a-checkbox-group v-model="rrule.byweekday" :options="weekdays" />
                </div>
            </div>
            <div v-if="showByMonth" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byMonth") }}:</span>
                <div class="options">
                    <a-checkbox-group v-model="rrule.bymonth" :options="months" />
                </div>
            </div>
            <div v-if="showByMonthDay" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byMonthDay") }}:</span>
                <div class="options">
                    <a-input v-model="rrule.bymonthday" />
                </div>
            </div>
            <div v-if="showByYearDay" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byYearDay") }}:</span>
                <div class="options">
                    <a-input v-model="rrule.byyearday" />
                </div>
            </div>
            <div v-if="showByWeekNo" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byWeekNo") }}:</span>
                <div class="options">
                    <a-input v-model="rrule.byweekno" />
                </div>
            </div>
            <div v-if="showByHour" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byHour") }}:</span>
                <div class="options">
                    <a-input v-model="rrule.byhour" />
                </div>
            </div>
            <div v-if="showByMinute" class="item-wrapper">
                <span class="label">{{ $t("ticimax.byMinute") }}:</span>
                <div class="options">
                    <a-input v-model="rrule.byminute" />
                </div>
            </div>
            <a-divider />
        </div>
        <div class="right">
            <h4 class="title">{{ $t("automation.schedulerRunningPlanEx") }}</h4>
            <ve-table
                :columns="getColumns()"
                :table-data="tableData"
                :row-style-option="rowStyleOption"
                :border-x="true"
                :border-y="true"
                row-key-field-name="_id"
                id="dates-table"
                :fixed-header="true"
                style="word-break: break-word"
                :clipboard-option="clipboardOption"
                :max-height="500"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { RRule, rrulestr } from "rrule";
import moment from "moment";
import log2 from "../../../../../../utils/log2";
import _ from "lodash";

const log = log2("RRuleGenerator");
export default Vue.extend({
    name: "RRuleGenerator",
    props: {
        value: {
            type: String,
            default: "",
        },
        maxCount: {
            type: Number,
            default: 365,
        },
    },
    data() {
        return {
            freqList: [
                { label: this.$t("ticimax.yearly"), value: RRule.YEARLY },
                { label: this.$t("ticimax.monthly"), value: RRule.MONTHLY },
                { label: this.$t("ticimax.weekly"), value: RRule.WEEKLY },
                { label: this.$t("ticimax.daily"), value: RRule.DAILY },
                { label: this.$t("ticimax.hourly"), value: RRule.HOURLY },
                { label: this.$t("ticimax.minutes"), value: RRule.MINUTELY },
            ],
            weekdays: [
                { label: this.$t("message.monday"), value: RRule.MO },
                { label: this.$t("message.tuesday"), value: RRule.TU },
                { label: this.$t("message.wednesday"), value: RRule.WE },
                { label: this.$t("message.thursday"), value: RRule.TH },
                { label: this.$t("message.friday"), value: RRule.FR },
                { label: this.$t("message.saturday"), value: RRule.SA },
                { label: this.$t("message.sunday"), value: RRule.SU },
            ],
            months: [
                { label: this.$t("ticimax.january"), value: 1 },
                { label: this.$t("ticimax.february"), value: 2 },
                { label: this.$t("ticimax.march"), value: 3 },
                { label: this.$t("ticimax.april"), value: 4 },
                { label: this.$t("ticimax.may"), value: 5 },
                { label: this.$t("ticimax.june"), value: 6 },
                { label: this.$t("ticimax.july"), value: 7 },
                { label: this.$t("ticimax.august"), value: 8 },
                { label: this.$t("ticimax.september"), value: 9 },
                { label: this.$t("ticimax.october"), value: 10 },
                { label: this.$t("ticimax.november"), value: 11 },
                { label: this.$t("ticimax.december"), value: 12 },
            ],

            rrule: {
                freq: RRule.DAILY,
                interval: 1,
                count: this.maxCount || 365,
                startDate: moment(),
                // tzid: "Europe/Istanbul",
                until: undefined,
                byweekday: undefined,
                bymonth: undefined,
                bymonthday: undefined,
                byyearday: undefined,
                byweekno: undefined,
                byhour: undefined,
                byminute: undefined,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            rowStyleOption: {
                stripe: true,
            },
            tableData: [],
        };
    },
    methods: {
        getColumns() {
            return [
                {
                    field: "",
                    key: "a",
                    title: "",
                    width: 50,
                    align: "center",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return ++rowIndex;
                    },
                },
                {
                    field: "dayName",
                    key: "dayName",
                    title: this.$t("ticimax.day"),
                    align: "left",
                    width: 150,
                },
                {
                    field: "day",
                    key: "day",
                    title: this.$t("ticimax.dayOfMonth"),
                    align: "left",
                    width: 100,
                },
                {
                    field: "month",
                    key: "month",
                    title: this.$t("ticimax.month"),
                    align: "left",
                    width: 100,
                },
                {
                    field: "year",
                    key: "year",
                    title: this.$t("ticimax.year"),
                    align: "left",
                    width: 100,
                },
                {
                    field: "time",
                    key: "time",
                    title: this.$t("ticimax.time"),
                    align: "left",
                    width: 100,
                },
            ];
        },
        getRRuleObject() {
            return {
                freq: this.rrule.freq,
                interval: this.rrule.interval,
                count: (this.rrule.count || 0) > this.maxCount ? this.maxCount : this.rrule.count || this.maxCount,
                dtstart: this.rrule.startDate?.toDate(),
                until: this.rrule.until?.toDate(),
                byweekday: this.rrule.byweekday,
                bymonth: this.rrule.bymonth,
                bymonthday: this.rrule.bymonthday
                    ? this.rrule.bymonthday.split(",").map((x) => parseInt(x))
                    : undefined,
                byyearday: this.rrule.byyearday ? this.rrule.byyearday.split(",").map((x) => parseInt(x)) : undefined,
                byweekno: this.rrule.byweekno ? this.rrule.byweekno.split(",").map((x) => parseInt(x)) : undefined,
                byhour: this.rrule.byhour ? this.rrule.byhour.split(",").map((x) => parseInt(x)) : undefined,
                byminute: this.rrule.byminute ? this.rrule.byminute.split(",").map((x) => parseInt(x)) : undefined,
            };
        },
        getItems() {
            const rruleObject = this.getRRuleObject();
            
            // Performans için count'u sınırla
            const limitedRruleObject = {
                ...rruleObject,
                count: Math.min(rruleObject.count || 10, 10) // Maksimum 10 örnek
            };

            try {
                const model = new RRule(limitedRruleObject);
                const result = model.all().map((date) => {
                    const d = moment(date);
                    return {
                        _id: d.valueOf(),
                        date,
                        dayName: d.format("dddd"),
                        day: d.format("DD"),
                        month: d.format("MMMM"),
                        year: d.format("YYYY"),
                        time: d.format("HH:mm")
                    };
                });

                log("RRule Dates Updated", result.length);
                return result;
            } catch (error) {
                log("RRule Generation Error", error);
                return [];
            }
        },
        parseRuleString(rruleString) {
            const rrule = rrulestr(rruleString);
            const rruleObject = {
                freq: rrule.options.freq,
                interval: rrule.options.interval,
                count: (rrule.options.count || 0) > 365 ? 365 : rrule.options.count || 100,
                startDate: rrule.options.dtstart ? moment(rrule.options.dtstart) : undefined,
                until: rrule.options.until ? moment(rrule.options.until) : undefined,
                byweekday: rrule.options.byweekday,
                bymonth: rrule.options.bymonth,
                bymonthday: rrule.options.bymonthday ? rrule.options.bymonthday.join(",") : undefined,
                byyearday: rrule.options.byyearday ? rrule.options.byyearday.join(",") : undefined,
                byweekno: rrule.options.byweekno ? rrule.options.byweekno.join(",") : undefined,
                byhour: rrule.options.byhour ? rrule.options.byhour.join(",") : undefined,
                byminute: rrule.options.byminute ? rrule.options.byminute.join(",") : undefined,
            };
            const currentRruleObject = this.getRRuleObject();
            if (!_.isEqual(rruleObject, currentRruleObject)) {
                this.rrule = rruleObject;
            }
            console.log("parseRuleString", {
                rruleString,
                currentRruleObject,
                rruleObject,
            });
        },
    },
    mounted() {
        this.parseRuleString(this.value);
    },
    watch: {
        rrule: {
            handler: function (val) {
                // Performans için debounce ekle
                this.$nextTick(() => {
                    try {
                        // RRule nesnesini oluştur
                        const rruleObject = this.getRRuleObject();
                        const model = new RRule(rruleObject);
                        const rrule = model.toString();
                        
                        // Input event'ini tetikle
                        this.$emit("input", rrule);
                        
                        // Tabloyu güncelle
                        this.tableData = this.getItems();
                    } catch (error) {
                        log("RRule Watch Error", error);
                        this.tableData = [];
                    }
                });
            },
            deep: true,
            immediate: true // Component oluşturulduğunda da çalışsın
        },
        value: {
            handler: function (val) {
                this.parseRuleString(val);
            },
        },
        'rrule.freq': {
            handler: function(newFreq) {
                // Frequency değiştiğinde ilgili by-fieldları temizle
                if (!this.showByWeekDay) this.rrule.byweekday = undefined;
                if (!this.showByMonth) this.rrule.bymonth = undefined;
                if (!this.showByMonthDay) this.rrule.bymonthday = undefined;
                if (!this.showByYearDay) this.rrule.byyearday = undefined;
                if (!this.showByWeekNo) this.rrule.byweekno = undefined;
                if (!this.showByHour) this.rrule.byhour = undefined;
                if (!this.showByMinute) this.rrule.byminute = undefined;
            },
            immediate: true
        },
    },
    computed: {
        showByWeekDay() {
            return [RRule.YEARLY, RRule.MONTHLY, RRule.WEEKLY].includes(this.rrule.freq);
        },
        showByMonth() {
            return [RRule.YEARLY].includes(this.rrule.freq);
        },
        showByMonthDay() {
            return [RRule.YEARLY, RRule.MONTHLY].includes(this.rrule.freq);
        },
        showByYearDay() {
            return [RRule.YEARLY].includes(this.rrule.freq);
        },
        showByWeekNo() {
            return [RRule.YEARLY].includes(this.rrule.freq);
        },
        showByHour() {
            return [RRule.YEARLY, RRule.MONTHLY, RRule.WEEKLY, RRule.DAILY].includes(this.rrule.freq);
        },
        showByMinute() {
            return [RRule.YEARLY, RRule.MONTHLY, RRule.WEEKLY, RRule.DAILY, RRule.HOURLY].includes(this.rrule.freq);
        }
    },
});
</script>

<style scoped lang="scss">
#dates-table {
}

.rrule-generator {
    display: flex;
    gap: 20px;

    .left {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 700px;
        border-right: 1px solid #ccc;
        padding-right: 10px;
        min-height: 500px;

        .item-wrapper {
            display: flex;
            gap: 10px;
            align-items: center;

            .label {
                font-weight: 700;
                width: 100px;
            }

            .options {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
            }
        }
    }

    .right {
        width: calc(100% - 700px);

        .items {
            display: flex;
            flex-direction: column;

            .item {
                display: flex;
                justify-content: space-between;
            }
        }
    }
}
</style>
